import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EditLink, IconChevron } from '../../../components/icons/WizardIcons';
import { CCR_EXPAND_SECTION_EVENT } from '../../../utils/collapsibleSection';

export function ReviewSection({
  step,
  title,
  children,
  defaultOpen = true,
  editTo,
}: {
  step: string;
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  editTo?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="ccr-sec ccr-review-sec">
      <button
        type="button"
        className="ccr-sh ccr-review-sh"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ccr-sh-t">
          {step}: {title}
        </span>
        <span className="ccr-sh-actions">
          {editTo ? <EditLink to={editTo} onClick={(e) => e.stopPropagation()} /> : null}
          <span className="ccr-sh-ic">
            <IconChevron expanded={open} />
          </span>
        </span>
      </button>
      <div className={`ccr-sb ccr-review-sb${open ? '' : ' is-collapsed'}`}>{children}</div>
    </div>
  );
}

export function ReviewSubBlock({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="ccr-review-block">
      {title ? (
        <div className="ccr-review-block-hdr">
          <h4 className="ccr-review-block-title">{title}</h4>
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function ReviewField({
  label,
  value,
  fullWidth,
  span,
}: {
  label: string;
  value: string | ReactNode;
  fullWidth?: boolean;
  span?: number;
}) {
  const spanClass = span === 2 ? ' span-2' : span === 3 ? ' span-3' : '';
  const displayValue =
    typeof value === 'string' ? (value.trim() ? value : '—') : value ?? '—';
  return (
    <div
      className={`ccr-review-field${fullWidth ? ' is-full' : ''}${spanClass}`}
      style={span && span > 1 ? { gridColumn: `span ${span}` } : undefined}
    >
      <div className="ccr-review-lbl">{label}</div>
      <div className="ccr-review-val">{displayValue}</div>
    </div>
  );
}

export function ReviewGrid({
  children,
  cols = 4,
}: {
  children: ReactNode;
  cols?: 2 | 3 | 4;
}) {
  return <div className={`ccr-review-grid cols-${cols}`}>{children}</div>;
}

export function ReviewListField({
  label,
  items,
  span,
}: {
  label: string;
  items: string[];
  span?: number;
}) {
  const spanClass = span === 2 ? ' span-2' : span === 3 ? ' span-3' : '';
  return (
    <div
      className={`ccr-review-field${spanClass}`}
      style={span && span > 1 ? { gridColumn: `span ${span}` } : undefined}
    >
      <div className="ccr-review-lbl">{label}</div>
      <div className="ccr-review-accused-list">
        {items.length ? (
          items.map((line, i) => (
            <p key={i} className="ccr-review-accused-line">
              {line}
            </p>
          ))
        ) : (
          <p className="ccr-review-val">—</p>
        )}
      </div>
    </div>
  );
}

export function ReviewChips({ items, label }: { items: string[]; label?: string }) {
  const { t } = useTranslation();
  const areaLabel = label ?? t('viewRequest.applicableAreas');
  return (
    <div className="ccr-review-field is-full">
      <div className="ccr-review-lbl">{areaLabel}</div>
      <div className="ccr-review-chips">
        {items.length ? (
          items.map((area) => (
            <span key={area} className="ccr-area-tag">
              {area}
            </span>
          ))
        ) : (
          <span className="ccr-review-val">—</span>
        )}
      </div>
    </div>
  );
}
